import { useEffect } from 'react';

/**
 * O tipo do Wake Lock, que o TypeScript desta versão ainda não traz.
 *
 * Declarar o mínimo aqui é melhor que `any`: o compilador continua conferindo
 * o que a gente chama, e o dia em que a `lib.dom` do TypeScript ganhar o tipo
 * de verdade, apagar isto é a única mudança.
 */
interface TrancaDeTela {
  released: boolean;
  release: () => Promise<void>;
}
interface ComWakeLock {
  wakeLock?: { request: (tipo: 'screen') => Promise<TrancaDeTela> };
}

/**
 * Segura a tela acesa enquanto a conversa estiver aberta.
 *
 * No computador o assunto é do sistema operacional; no tablet e no celular,
 * não: a tela apaga em trinta segundos de leitura, e acompanhar uma sessão é
 * justamente ficar olhando sem tocar em nada. Apagar no meio de um turno
 * obrigava a acordar o aparelho a cada minuto — e, antes do conserto da
 * reconexão, ainda custava um F5.
 *
 * Três cuidados que o Wake Lock exige e que não são opcionais:
 *
 * - **O navegador solta a tranca sozinho** quando a aba deixa de ser visível,
 *   e não a devolve na volta. Sem reatar no `visibilitychange`, ela vale uma
 *   vez só — some no primeiro alt-tab e nunca mais existe.
 *   Erro que também acontece de dentro do gesto do usuário; sem o `catch`
 *   síncrono, a promessa rejeitada vira erro não tratado no console.
 * - **Precisa de HTTPS e de aba visível.** Pedir com a aba escondida rejeita;
 *   por isso o pedido confere a visibilidade antes.
 * - **Nem todo navegador tem.** Sem `wakeLock`, o gancho não faz nada — a tela
 *   apaga como antes, e nada quebra.
 */
export function usarTelaAcesa(ativo: boolean): void {
  useEffect(() => {
    const api = (navigator as unknown as ComWakeLock).wakeLock;
    if (!ativo || !api) return;

    let tranca: TrancaDeTela | null = null;
    let vivo = true;

    const pedir = (): void => {
      if (!vivo || document.visibilityState !== 'visible') return;
      if (tranca && !tranca.released) return;
      void api
        .request('screen')
        .then((nova) => {
          // A montagem pode ter acabado enquanto a promessa estava no ar: sem
          // esta guarda, a tranca ficaria presa depois de a tela fechar.
          if (!vivo) return void nova.release().catch(() => undefined);
          tranca = nova;
        })
        .catch(() => undefined);
    };

    const aoVoltar = (): void => pedir();
    document.addEventListener('visibilitychange', aoVoltar);
    pedir();

    return () => {
      vivo = false;
      document.removeEventListener('visibilitychange', aoVoltar);
      if (tranca && !tranca.released) void tranca.release().catch(() => undefined);
    };
  }, [ativo]);
}
